import { FC, ReactNode } from 'react';
import { Typography } from '@mui/material';
import { COLORS } from '@cellebrite/saas-ui-common';
import { TypographyProps } from '@mui/material';

interface MultiMarkedTextProps {
    children: ReactNode;
    searchTextArr: string[];
    containerProps?: Omit<TypographyProps, 'dangerouslySetInnerHTML' | 'children'>;
}

const MultiMarkedText: FC<MultiMarkedTextProps> = ({ children, searchTextArr = [], containerProps }) => {
    if (!children) {
        return null;
    }

    const text = children as string;
    if (!text || !searchTextArr.length) {
        return (
            <Typography noWrap {...containerProps}>
                {text}
            </Typography>
        );
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${text}</div>`, 'text/html');
    const textNodes = [];

    const getTextNodes = (node) => {
        if (node.nodeType === 3) {
            textNodes.push(node);
        } else {
            node.childNodes.forEach((child) => getTextNodes(child));
        }
    };

    getTextNodes(doc.body.firstChild);

    textNodes.forEach((node) => {
        const content = node.textContent;

        const potentialMatches = [];

        const sortedTerms = searchTextArr.filter((term) => term && term.trim()).sort((a, b) => b.length - a.length);

        sortedTerms.forEach((term, termIndex) => {
            let currentIndex = 0;
            const termMatches = [];

            while (currentIndex < content.length) {
                const index = content.toLowerCase().indexOf(term.toLowerCase(), currentIndex);
                if (index === -1) break;

                const beforeChar = index > 0 ? content[index - 1] : '';
                const afterChar = index + term.length < content.length ? content[index + term.length] : '';

                const isWordStart = index === 0 || /[\s.,;:!?()[\]{}"'\-_=+<>/\\|~`@#$%^&*]/g.test(beforeChar);
                const isWordEnd =
                    index + term.length === content.length ||
                    /[\s.,;:!?()[\]{}"'\-_=+<>/\\|~`@#$%^&*]/g.test(afterChar);

                if (isWordStart && isWordEnd) {
                    termMatches.push({
                        start: index,
                        end: index + term.length,
                        text: content.substring(index, index + term.length),
                        termIndex: termIndex,
                    });
                }

                currentIndex = index + 1;
            }

            if (termMatches.length > 0) {
                potentialMatches.push(...termMatches);
            }
        });

        potentialMatches.sort((a, b) => {
            if (a.start !== b.start) return a.start - b.start;
            return b.end - b.start - (a.end - a.start);
        });

        const finalMatches = [];
        const coveredRanges = [];

        potentialMatches.forEach((match) => {
            const isSubsumed = coveredRanges.some((range) => range.start <= match.start && range.end >= match.end);

            if (!isSubsumed) {
                finalMatches.push(match);
                coveredRanges.push({ start: match.start, end: match.end });
            }
        });

        const markedRanges = [];
        const markedPositions = new Array(content.length).fill(false);

        finalMatches.forEach((match) => {
            let canMark = true;
            for (let i = match.start; i < match.end; i++) {
                if (markedPositions[i]) {
                    canMark = false;
                    break;
                }
            }

            if (canMark) {
                for (let i = match.start; i < match.end; i++) {
                    markedPositions[i] = true;
                }
                markedRanges.push(match);
            }
        });

        markedRanges.sort((a, b) => a.start - b.start);

        const spans = [];
        let lastIndex = 0;

        markedRanges.forEach((range) => {
            if (range.start > lastIndex) {
                spans.push(content.substring(lastIndex, range.start));
            }

            const span = document.createElement('mark');
            span.style.backgroundColor = COLORS.NEGATIVE_SOFT;
            span.textContent = content.substring(range.start, range.end);
            spans.push(span.outerHTML);

            lastIndex = range.end;
        });

        if (lastIndex < content.length) {
            spans.push(content.substring(lastIndex));
        }

        const fragment = parser.parseFromString(`<span>${spans.join('')}</span>`, 'text/html').body.firstChild;
        node.parentNode.replaceChild(fragment, node);
    });

    return (
        <Typography
            noWrap
            {...containerProps}
            dangerouslySetInnerHTML={{ __html: (doc.body.firstChild as HTMLElement).innerHTML }}
        ></Typography>
    );
};

export default MultiMarkedText;
